// AGC: FarmProHub Proposals Page
// AGC: Governance voting interface - view and vote on proposals

import React, { useState } from 'react'
import { useAccount } from 'wagmi'
import { motion } from 'framer-motion'
import { Vote, Clock, CheckCircle, XCircle, AlertCircle, ThumbsUp, ThumbsDown, Users, Plus, Link2, Gift, User } from 'lucide-react'
import { useGovernance } from '../hooks/useGovernance'
import { useUserType } from '../hooks/useUserType'

interface Proposal {
  id: string
  title: string
  description: string
  status: 'active' | 'passed' | 'rejected' | 'pending'
  votesFor: number
  votesAgainst: number
  totalVoters: number
  endTime: number
  createdAt: number
  // AGC: Forum post and reward fields
  forumPostUrl?: string
  ideaOriginator?: string
  rewardAmount?: string
}

export const ProposalsPage: React.FC = () => {
  const { address, isConnected } = useAccount()
  const { createProposal, isCreating } = useGovernance()
  const userType = useUserType()
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  
  // AGC: Create proposal form state
  const [newProposal, setNewProposal] = useState({
    title: '',
    description: '',
    forumPostUrl: '',
    ideaOriginator: '',
    rewardAmount: '',
  })

  // AGC: TODO: Fetch real proposals from contract
  const proposals: Proposal[] = [
    // AGC: Empty for now - will be populated from contract
  ]

  // AGC: Handle create proposal
  const handleCreateProposal = async () => {
    if (!newProposal.title.trim() || !newProposal.description.trim()) {
      alert('Please fill in title and description')
      return
    }

    if (newProposal.forumPostUrl && !newProposal.ideaOriginator) {
      alert('If you link a forum post, you must specify the idea originator address')
      return
    }

    if (newProposal.ideaOriginator && !/^0x[a-fA-F0-9]{40}$/.test(newProposal.ideaOriginator)) {
      alert('Invalid Ethereum address for idea originator')
      return
    }

    try {
      await createProposal({
        title: newProposal.title,
        description: newProposal.description,
        forumPostUrl: newProposal.forumPostUrl || undefined,
        ideaOriginator: newProposal.ideaOriginator || undefined,
        rewardAmount: newProposal.rewardAmount ? BigInt(newProposal.rewardAmount) : undefined,
      })
      
      // AGC: Reset form and close modal
      setNewProposal({
        title: '',
        description: '',
        forumPostUrl: '',
        ideaOriginator: '',
        rewardAmount: '',
      })
      setShowCreateModal(false)
    } catch (error) {
      console.error('AGC: Error creating proposal:', error)
      alert(`Failed to create proposal: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  // AGC: Truncate address for display
  const truncateAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`
  }

  const getStatusIcon = (status: Proposal['status']) => {
    switch (status) {
      case 'active':
        return <Clock className="w-4 h-4 text-yellow-400" />
      case 'passed':
        return <CheckCircle className="w-4 h-4 text-[#0072CE]" />
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-400" />
      case 'pending':
        return <AlertCircle className="w-4 h-4 text-blue-400" />
      default:
        return null
    }
  }

  const getStatusColor = (status: Proposal['status']) => {
    switch (status) {
      case 'active':
        return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30'
      case 'passed':
        return 'text-[#0072CE] bg-[#0072CE]/10 border-[#0072CE]/30'
      case 'rejected':
        return 'text-red-400 bg-red-400/10 border-red-400/30'
      case 'pending':
        return 'text-blue-400 bg-blue-400/10 border-blue-400/30'
      default:
        return ''
    }
  }

  return (
    <main className="pt-[72px] pb-[88px] px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* AGC: Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-4 relative"
        >
          {/* AGC: User Type Badge - Top Right */}
          {isConnected && (
            <div className="absolute top-0 right-0">
              <div className={`${userType.bgColor} ${userType.borderColor} border rounded-lg px-3 py-1.5 flex items-center gap-2`}>
                <span className={`text-xs font-semibold ${userType.color}`}>
                  Type: {userType.label}
                </span>
              </div>
            </div>
          )}
          <h1 className={`text-2xl font-bold text-white mb-2 flex items-center justify-center gap-2 ${isConnected ? 'pt-10' : ''}`}>
            <Vote className="w-7 h-7 text-blue-400" />
            Governance
          </h1>
          <p className="text-slate-400 text-sm">
            Vote on proposals to shape the future of FarmProHub
          </p>
        </motion.div>

        {/* AGC: Create Proposal Button - Only for Singers */}
        {isConnected && userType.canCreateProposal && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create New Proposal
            </button>
          </motion.div>
        )}
        {isConnected && !userType.canCreateProposal && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 text-center"
          >
            <p className="text-slate-400 text-sm">
              {userType.type === 'player' 
                ? 'Stake tokens to vote on proposals. Stake 1000+ OGB to create proposals.'
                : 'Stake 1000+ OGB to create proposals.'}
            </p>
          </motion.div>
        )}

        {/* AGC: Voting Power Info */}
        {isConnected && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-r from-blue-900/30 to-indigo-900/30 rounded-xl p-5 border border-blue-500/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Your Voting Power</p>
                <p className="text-2xl font-bold text-white">0 FP</p>
                <p className="text-xs text-slate-500">Based on your staked amount</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-400">Votes Cast</p>
                <p className="text-2xl font-bold text-blue-400">0</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* AGC: Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-3 gap-3"
        >
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 text-center">
            <Clock className="w-5 h-5 text-yellow-400 mx-auto mb-2" />
            <p className="text-xl font-bold text-white">0</p>
            <p className="text-xs text-slate-400">Active</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 text-center">
            <CheckCircle className="w-5 h-5 text-[#0072CE] mx-auto mb-2" />
            <p className="text-xl font-bold text-white">0</p>
            <p className="text-xs text-slate-400">Passed</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 text-center">
            <Users className="w-5 h-5 text-blue-400 mx-auto mb-2" />
            <p className="text-xl font-bold text-white">0</p>
            <p className="text-xs text-slate-400">Total</p>
          </div>
        </motion.div>

        {/* AGC: Proposals List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-4"
        >
          <h3 className="text-white font-semibold">All Proposals</h3>
          
          {proposals.length === 0 ? (
            <div className="bg-slate-800/50 rounded-xl p-8 border border-slate-700/50 text-center">
              <Vote className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h4 className="text-white font-semibold mb-2">No Proposals Yet</h4>
              <p className="text-slate-400 text-sm">
                There are no governance proposals at this time.
              </p>
              <p className="text-slate-500 text-xs mt-2">
                Proposals can only be created by the contract owner.
              </p>
            </div>
          ) : (
            proposals.map((proposal) => (
              <div
                key={proposal.id}
                className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50 hover:border-slate-600 transition-colors cursor-pointer"
                onClick={() => setSelectedProposal(proposal)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="text-white font-semibold mb-1">{proposal.title}</h4>
                    <p className="text-slate-400 text-sm line-clamp-2">{proposal.description}</p>
                    
                    {/* AGC: Forum post and reward info */}
                    {(proposal.forumPostUrl || proposal.ideaOriginator) && (
                      <div className="mt-2 flex flex-wrap gap-3 text-xs">
                        {proposal.forumPostUrl && (
                          <a
                            href={proposal.forumPostUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
                          >
                            <Link2 className="w-3 h-3" />
                            Forum Post
                          </a>
                        )}
                        {proposal.ideaOriginator && (
                          <span className="flex items-center gap-1 text-slate-400">
                            <User className="w-3 h-3" />
                            Originator: {truncateAddress(proposal.ideaOriginator)}
                          </span>
                        )}
                        {proposal.rewardAmount && (
                          <span className="flex items-center gap-1 text-[#0072CE]">
                            <Gift className="w-3 h-3" />
                            Reward: {proposal.rewardAmount} FP
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(proposal.status)} flex items-center gap-1`}>
                    {getStatusIcon(proposal.status)}
                    {proposal.status}
                  </span>
                </div>
                
                {/* AGC: Vote Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-slate-400 mb-1">
                    <span className="flex items-center gap-1">
                      <ThumbsUp className="w-3 h-3 text-[#0072CE]" />
                      {proposal.votesFor} For
                    </span>
                    <span className="flex items-center gap-1">
                      <ThumbsDown className="w-3 h-3 text-red-400" />
                      {proposal.votesAgainst} Against
                    </span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#0072CE] to-[#005a9e]"
                      style={{
                        width: `${proposal.votesFor + proposal.votesAgainst > 0 
                          ? (proposal.votesFor / (proposal.votesFor + proposal.votesAgainst)) * 100 
                          : 0}%`
                      }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {proposal.totalVoters} voters • 51% threshold to pass
                  </p>
                </div>
              </div>
            ))
          )}
        </motion.div>

        {/* AGC: How Voting Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50"
        >
          <h3 className="text-white font-semibold mb-3">How Voting Works</h3>
          <ul className="space-y-2 text-sm text-slate-400">
            <li className="flex items-start gap-2">
              <span className="text-blue-400">1.</span>
              Only stakers can vote - voting power equals staked amount
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400">2.</span>
              Proposals need 51% of votes to pass
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400">3.</span>
              Voting period lasts 7 days after proposal creation
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400">4.</span>
              Passed proposals are executed by the contract owner
            </li>
          </ul>
        </motion.div>

        {/* AGC: Create Proposal Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-slate-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-blue-400" />
                Create New Proposal
              </h3>
              
              <div className="space-y-4">
                {/* AGC: Title */}
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">
                    Title <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={newProposal.title}
                    onChange={(e) => setNewProposal({ ...newProposal, title: e.target.value })}
                    placeholder="Enter proposal title..."
                    maxLength={200}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                  />
                </div>

                {/* AGC: Description */}
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">
                    Description <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={newProposal.description}
                    onChange={(e) => setNewProposal({ ...newProposal, description: e.target.value })}
                    placeholder="Describe your proposal in detail..."
                    maxLength={5000}
                    rows={6}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
                  />
                </div>

                {/* AGC: Forum Post URL */}
                <div>
                  <label className="text-sm text-slate-400 mb-2 block flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    Forum Post URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={newProposal.forumPostUrl}
                    onChange={(e) => setNewProposal({ ...newProposal, forumPostUrl: e.target.value })}
                    placeholder="https://forum.example.com/posts/123"
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Link to the forum post where this idea was discussed
                  </p>
                </div>

                {/* AGC: Idea Originator */}
                <div>
                  <label className="text-sm text-slate-400 mb-2 block flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Idea Originator Address (Optional)
                  </label>
                  <input
                    type="text"
                    value={newProposal.ideaOriginator}
                    onChange={(e) => setNewProposal({ ...newProposal, ideaOriginator: e.target.value })}
                    placeholder="0x..."
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    The wallet address of the user who created the forum post/idea. Required if forum post URL is provided.
                  </p>
                </div>

                {/* AGC: Reward Amount */}
                <div>
                  <label className="text-sm text-slate-400 mb-2 block flex items-center gap-2">
                    <Gift className="w-4 h-4" />
                    Reward Amount (FP Tokens) (Optional)
                  </label>
                  <input
                    type="number"
                    value={newProposal.rewardAmount}
                    onChange={(e) => setNewProposal({ ...newProposal, rewardAmount: e.target.value })}
                    placeholder="1000"
                    min="0"
                    step="1"
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Token amount to reward the idea originator if this proposal passes. Leave empty for no reward.
                  </p>
                </div>

                {/* AGC: Info Box */}
                <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-3">
                  <p className="text-xs text-blue-300">
                    <strong>Note:</strong> If you link a forum post and set a reward amount, the idea originator will automatically receive the tokens when this proposal passes.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false)
                    setNewProposal({
                      title: '',
                      description: '',
                      forumPostUrl: '',
                      ideaOriginator: '',
                      rewardAmount: '',
                    })
                  }}
                  className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateProposal}
                  disabled={!newProposal.title.trim() || !newProposal.description.trim() || isCreating}
                  className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Create Proposal
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </main>
  )
}

